import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faHistory,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../common/RefreshButton";
import ReadOnlyBanner from "../../common/ReadOnlyBanner";
import ImageUpload from "./ImageUpload";
import ImageAnalysisResults from "./ImageAnalysisResults";
import ImageAnalysisDetails from "./ImageAnalysisDetails";
import ImageAnalysisMobile from "./ImageAnalysisMobile";
import ImageAnalysisAPI from "../../services/ImageAnalysisAPI";
import { useLoading } from "../../../../contexts/LoadingContext";
import pollForResult from "../../../../utils/pollForResult";
import EmptyStatePanel from "../common/EmptyStatePanel";
import "./css/ImageAnalysisSimple.css";
import "../../css/Common.css";
import HelpGuideButton from "../../HelpGuideButton";
import DisclosuresModal from "../common/DisclosuresModal";

/**
 * Main container for Image Upload Scam Detection feature
 * Similar to EmailAnalysisSimple container
 */
const ImageAnalysisSimple = () => {
  const { showLoading, hideLoading } = useLoading();
  const { t } = useTranslation();
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [waitingForAnalysis, setWaitingForAnalysis] = useState(false);

  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Load analyses on mount
  useEffect(() => {
    loadAnalyses();
    loadStatistics();
  }, []);

  /**
   * Load all analyses
   */
  const MAX_ANALYSES = 4;

  const loadAnalyses = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ImageAnalysisAPI.searchAnalyses({});

      // Enforce 4-item history limit: delete oldest excess analyses
      if (data && data.length > MAX_ANALYSES) {
        const sorted = [...data].sort(
          (a, b) => new Date(b.analyzedAt) - new Date(a.analyzedAt),
        );
        const excess = sorted.slice(MAX_ANALYSES);
        for (const item of excess) {
          try {
            await ImageAnalysisAPI.deleteAnalysis(item.uploadId);
          } catch (e) {
            console.error("Error deleting excess analysis:", e);
          }
        }
        setAnalyses(sorted.slice(0, MAX_ANALYSES));
      } else {
        setAnalyses(data);
      }
    } catch (err) {
      setError(err.message || "Failed to load analyses");
      console.error("Error loading analyses:", err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load statistics
   */
  const loadStatistics = async () => {
    try {
      const stats = await ImageAnalysisAPI.getStatistics();
      setStatistics(stats);
    } catch (err) {
      console.error("Error loading statistics:", err);
    }
  };

  /**
   * Handle upload complete
   */
  const handleUploadComplete = (result) => {
    // Show loading modal while waiting for AI analysis
    showLoading(
      "Analyzing image with AI...",
      "AI is analyzing your image. This may take up to 2 minutes...",
    );
    toast.success(t("imageAnalysis.toast.uploadSuccess"));

    // Poll for analysis completion using the shared polling utility
    const uploadId = result?.uploadId;

    pollForResult(
      async () => {
        const exists = uploadId
          ? await ImageAnalysisAPI.analysisExists(uploadId)
          : false;
        return { done: exists };
      },
      {
        interval: 10000,
        maxPolls: 40,
        timeoutMessage:
          "Analysis is taking longer than expected. Please refresh in a moment.",
      },
    )
      .then(async () => {
        await loadAnalyses();
        await loadStatistics();
        hideLoading();
        toast.success(t("imageAnalysis.toast.checksComplete"));
      })
      .catch(async () => {
        await loadAnalyses();
        await loadStatistics();
        hideLoading();
        toast(
          "Analysis is taking longer than expected. Please refresh in a moment.",
          { icon: "ℹ️" },
        );
      });
  };

  /**
   * Handle manual refresh
   */
  const handleRefresh = async () => {
    try {
      setLoading(true);
      await loadAnalyses();
      await loadStatistics();
      toast.success(t("imageAnalysis.toast.historyRefreshed"));
    } catch (err) {
      toast.error(
        t("imageAnalysis.toast.refreshFailed").replace("{error}", err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle view analysis details
   */
  const handleViewDetails = (analysis) => {
    // Use the analysis data from the list directly
    // The search endpoint returns complete data, no need to fetch again
    setSelectedAnalysis(analysis);
  };

  /**
   * Handle back to list
   */
  const handleBackToList = () => {
    setSelectedAnalysis(null);
  };

  /**
   * Handle delete analysis
   */
  const handleDeleteAnalysis = async (uploadId) => {
    try {
      await ImageAnalysisAPI.deleteAnalysis(uploadId);

      // Refresh list
      await loadAnalyses();
      await loadStatistics();

      // If viewing deleted item, go back to list
      if (selectedAnalysis && selectedAnalysis.uploadId === uploadId) {
        handleBackToList();
      }
    } catch (err) {
      toast.error(
        "Failed to delete analysis: " + (err.message || "Unknown error"),
      );
    }
  };

  /**
   * Handle filter change
   */
  const handleFilterChange = async (filterType) => {
    try {
      setLoading(true);
      setError(null);

      let data;
      switch (filterType) {
        case "all":
          data = await ImageAnalysisAPI.searchAnalyses({});
          break;
        case "scams":
          data = await ImageAnalysisAPI.getScamAnalyses();
          break;
        case "safe":
          data = await ImageAnalysisAPI.getSafeAnalyses();
          break;
        case "recent":
          data = await ImageAnalysisAPI.getRecentAnalyses(7);
          break;
        default:
          data = await ImageAnalysisAPI.searchAnalyses({});
      }

      setAnalyses(data);
    } catch (err) {
      setError(err.message || "Failed to filter analyses");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <ReadOnlyBanner />
      {isMobile ? (
        <ImageAnalysisMobile />
      ) : (
        <div className="common-container image-analysis-container">
          {/* Header */}
          <div className="common-header">
            <h1 className="main-title">Screenshot Analysis</h1>
            <div className="subtitle-with-help">
              <p className="common-subtitle">
                Upload screenshots to detect scams, phishing, and fraudulent
                content using AI-powered analysis
              </p>
              <HelpGuideButton guideId={6} />
            </div>
          </div>

          {/* Main Panels Container */}
          <div className="panels-container">
            {/* Upload Section - Compact, at top of panels */}
            <div className="upload-section-compact">
              <ImageUpload onUploadComplete={handleUploadComplete} compact />
            </div>

            {/* Two Panel Layout */}
            <div className="common-layout">
              {/* Left Panel - Analysis History */}
              <div className="analysis-form-container" style={{ flex: 1 }}>
                <div className="common-section-header">
                  <h3>
                    <FontAwesomeIcon icon={faHistory} /> Analysis History
                  </h3>
                  <RefreshButton
                    onClick={handleRefresh}
                    variant="header"
                    loading={loading}
                  />
                </div>
                <div className="panel-content">
                  <ImageAnalysisResults
                    analyses={analyses}
                    loading={loading}
                    error={error}
                    selectedAnalysis={selectedAnalysis}
                    onViewDetails={handleViewDetails}
                    onDelete={handleDeleteAnalysis}
                    onFilterChange={handleFilterChange}
                  />
                </div>
              </div>

              {/* Right Panel - Analysis Details */}
              <div className="common-content" style={{ flex: 1 }}>
                {selectedAnalysis ? (
                  <ImageAnalysisDetails
                    analysis={selectedAnalysis}
                    onBack={handleBackToList}
                    onDelete={() =>
                      handleDeleteAnalysis(selectedAnalysis.uploadId)
                    }
                  />
                ) : (
                  <div className="analysis-details">
                    <div className="common-section-header">
                      <h3>
                        <FontAwesomeIcon icon={faShieldAlt} /> Analysis Details
                      </h3>
                    </div>
                    <div className="panel-content">
                      <EmptyStatePanel
                        icon={faImage}
                        title="No Analysis Selected"
                        description={
                          analyses.length === 0
                            ? "Upload a screenshot above to get started with AI-powered scam detection."
                            : "Select an analysis from the history to view detailed results."
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DisclosuresModal disclaimerText="The screenshot analysis provided is for educational purposes only. For complete terms and limitations, please review our" />
        </div>
      )}
    </>
  );
};

export default ImageAnalysisSimple;
